import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Heading, Text } from "@nbs/ui";
import { STORY_LANDSCAPE_CHANGE_QUERY, STORY_YEARS } from "../api/documents";
import { STUDY_LANDSCAPES, type LandscapeId } from "../data/landscapes";
import {
  filterLandCoverRowsByPublishedYears,
  landCoverQueryForLandscape,
  publishedLandCoverYears,
} from "../data/services";
import {
  LAND_COVER_PRESSURE_LABELS,
  toLandCoverChart,
  toLandCoverPressureShares,
} from "@/features/analytics/utils/land-cover";
import { summarizeLandCoverTrend } from "@/features/analytics/utils/land-cover-summary";
import { NbsAreaChart } from "@/features/charts/NbsAreaChart";
import { NbsChartContainer } from "@/features/charts/NbsChartContainer";
import { NbsDonutChart } from "@/features/charts/NbsDonutChart";
import { LandscapePicker } from "./LandscapePicker";
import { STORY_IMAGES, StoryMedia } from "./StoryMedia";

/** Area-chart palette (occupation classes). */
const LAND_COVER_COLORS = [
  "#1f6f4a",
  "#18753c",
  "#2a9d6a",
  "#0b6e4f",
  "#8a5a1a",
  "#3d87b8",
  "#c47b2c",
  "#5c6b63",
] as const;

/** Threat reading: warm for pressures, green only for remaining natural cover. */
const PRESSURE_DONUT_COLORS = [
  "#b34000", // cultures
  "#c47b2c", // habité
  "#8a5a1a", // infrastructures
  "#1f6f4a", // milieux naturels
] as const;

function formatYearList(years: readonly number[]): string {
  if (years.length === 0) return "";
  if (years.length === 1) return `en ${years[0]}`;
  if (years.length === 2) return `en ${years[0]} et ${years[1]}`;
  return `en ${years.slice(0, -1).join(", ")} et ${years[years.length - 1]}`;
}

function pressureDonutColors(segmentNames: readonly string[]): string[] {
  return segmentNames.map((name) => {
    if (name === LAND_COVER_PRESSURE_LABELS.cultures) return PRESSURE_DONUT_COLORS[0]!;
    if (name === LAND_COVER_PRESSURE_LABELS.habite) return PRESSURE_DONUT_COLORS[1]!;
    if (name === LAND_COVER_PRESSURE_LABELS.infrastructure) return PRESSURE_DONUT_COLORS[2]!;
    return PRESSURE_DONUT_COLORS[3]!;
  });
}

type StoryChangeStepProps = {
  readonly selectedId: LandscapeId;
  readonly onSelect: (id: LandscapeId) => void;
  readonly onBack: () => void;
  readonly onContinue: () => void;
};

export function StoryChangeStep({
  selectedId,
  onSelect,
  onBack,
  onContinue,
}: StoryChangeStepProps) {
  const landCoverQ = landCoverQueryForLandscape(selectedId);
  const placeLabel = STUDY_LANDSCAPES.find((p) => p.id === selectedId)?.label ?? selectedId;

  const { data, loading, error, refetch } = useQuery(STORY_LANDSCAPE_CHANGE_QUERY, {
    variables: {
      // Madina-Oula series live on the zone emprise; paysage has none.
      territoryIds: [landCoverQ.territoryId],
      dateFrom: "2005-01-01",
      dateTo: "2025-12-31",
      // Surfaces (ha) for the evolution chart — all study landscapes publish this metric.
      metric: "superficie",
    },
    // Avoid colliding with other story steps' dashboardKpis selection sets in the shared cache.
    fetchPolicy: "no-cache",
  });

  const landCover = data?.landCoverSeries?.[0];

  const landCoverChart = useMemo(() => {
    const chart = toLandCoverChart(landCover?.points ?? []);
    return {
      ...chart,
      byYear: filterLandCoverRowsByPublishedYears(chart.byYear, selectedId),
      years: publishedLandCoverYears(
        chart.years.map((y) => Number(y)).filter((y) => Number.isFinite(y)),
        selectedId,
      ).map(String),
    };
  }, [landCover, selectedId]);

  const observationYears = useMemo(() => {
    const fromChart = landCoverChart.years.map((y) => Number(y)).filter((y) => Number.isFinite(y));
    const years = fromChart.length > 0 ? fromChart : [...STORY_YEARS];
    return publishedLandCoverYears(years, selectedId);
  }, [landCoverChart.years, selectedId]);

  const yearList = formatYearList(observationYears);

  const seriesKeys = landCoverChart.classes.slice(0, 8);

  const trendSummary = useMemo(
    () => summarizeLandCoverTrend(landCoverChart.byYear, seriesKeys),
    [landCoverChart.byYear, seriesKeys],
  );

  const yearSnapshots = useMemo(
    () => observationYears.map((year) => toLandCoverPressureShares(landCoverChart.byYear, year)),
    [landCoverChart.byYear, observationYears],
  );

  return (
    <section
      className="nbs-story-step nbs-story-dash"
      data-testid="story-change-step"
      aria-labelledby="story-change-heading"
    >
      <header className="nbs-story-dash__intro">
        <div className="nbs-story-dash__intro-title">
          <p className="nbs-story-step__eyebrow">Étape 4 · Changements</p>
          <Heading level={1} id="story-change-heading">
            Quelles sont les tendances ?
          </Heading>
          <p className="nbs-story-step__question">Que s’est-il passé entre 2005 et 2025 ?</p>
        </div>
        <div className="nbs-story-dash__intro-aside">
          <StoryMedia
            src={STORY_IMAGES.vegetation}
            alt="Inspection d’une récolte de maïs, photo CECI"
            caption={`Occupation du sol et usages agricoles — ${placeLabel}`}
            ratio="card"
          />
          <Text className="nbs-story-step__lead">
            Évolution de l’occupation du sol à {placeLabel} — ce qui pèse sur la nature (cultures,
            habitat, infrastructures){yearList ? `, ${yearList}` : ""}.
          </Text>
          <LandscapePicker selectedId={selectedId} onSelect={onSelect} />
        </div>
      </header>

      {error ? (
        <div className="nbs-story-dash__error" role="alert">
          <Text>Impossible de charger les séries d’occupation des sols.</Text>
          <button type="button" className="nbs-story-step__cta" onClick={() => void refetch()}>
            Réessayer
          </button>
        </div>
      ) : null}

      <aside className="nbs-story-dash__summary" aria-label="Lecture des tendances">
        <p className="nbs-story-dash__summary-label">À retenir sur la période</p>
        <p>{trendSummary}</p>
      </aside>

      <div className="nbs-story-dash__change-stack">
        <NbsChartContainer
          title={
            observationYears.length > 0
              ? `Occupation des sols ${observationYears.join(" · ")}`
              : "Occupation des sols"
          }
          description="Surfaces par classe d’occupation — affichage superposé comme sur le tableau de bord."
          unit={landCover?.unit}
          territory={placeLabel}
          source="nbs-ind-superficie-occupation-sol"
          isLoading={loading && !landCover}
          empty={!loading && landCoverChart.byYear.length === 0}
          emptyMessage="Pas encore de série d’occupation des sols pour ce territoire."
        >
          <NbsAreaChart
            data={landCoverChart.byYear}
            xKey="year"
            seriesKeys={seriesKeys}
            stacked={false}
            unit={landCover?.unit}
            height={340}
            colors={LAND_COVER_COLORS}
          />
        </NbsChartContainer>

        <section
          className="nbs-story-dash__snapshots"
          data-testid="story-change-snapshots"
          aria-labelledby="story-change-snapshots-heading"
        >
          <header className="nbs-story-dash__snapshots-head">
            <Heading level={2} id="story-change-snapshots-heading">
              Pressions sur la nature
              {observationYears.length > 0 ? ` · ${observationYears.join(" · ")}` : ""}
            </Heading>
            <p>
              Part du territoire sous pressions anthropiques (cultures, zones habitées,
              infrastructures) — {placeLabel}.
            </p>
          </header>

          <div className="nbs-story-dash__donuts">
            {yearSnapshots.map((snapshot) => {
              const empty = !loading && snapshot.segments.length === 0;
              const threat =
                snapshot.pressurePct >= 20 ? "high" : snapshot.pressurePct >= 10 ? "mid" : "low";
              return (
                <article
                  key={snapshot.year}
                  className="nbs-story-dash__donut-card"
                  data-threat={threat}
                  data-testid={`story-change-donut-${snapshot.year}`}
                >
                  <h3 className="nbs-story-dash__donut-year">{snapshot.year}</h3>
                  <p className="nbs-story-dash__donut-threat-label">
                    {snapshot.pressurePct > 0
                      ? `${new Intl.NumberFormat("fr-FR", {
                          maximumFractionDigits: 1,
                        }).format(snapshot.pressurePct)} % sous pression`
                      : "Pressions non mesurées"}
                  </p>
                  {loading && !landCover ? (
                    <p className="nbs-story-dash__donut-empty">Chargement…</p>
                  ) : empty ? (
                    <p className="nbs-story-dash__donut-empty">
                      Aucune répartition pour cette année.
                    </p>
                  ) : (
                    <NbsDonutChart
                      data={snapshot.segments}
                      height={220}
                      colors={pressureDonutColors(snapshot.segments.map((s) => s.name))}
                      showLegend
                      centerLabel="Sous pression"
                      centerValue={new Intl.NumberFormat("fr-FR", {
                        maximumFractionDigits: 1,
                      }).format(snapshot.pressurePct)}
                      centerUnit="%"
                    />
                  )}
                </article>
              );
            })}
          </div>
        </section>
      </div>

      <div className="nbs-story-step__actions">
        <button
          type="button"
          className="nbs-story-step__cta nbs-story-step__cta--ghost"
          onClick={onBack}
        >
          Retour : méthode
        </button>
        <button type="button" className="nbs-story-step__cta" onClick={onContinue}>
          Suite : ce que disent les communautés
        </button>
      </div>
    </section>
  );
}
